import React, { useState, useRef, useEffect, useCallback } from 'react';

import { VideoSpeedIcon } from '@/assets/icons/v2/VideoSpeed';
import { ICON_SIZE, VELOCITY_OPTIONS } from '../../utils/constants';

import * as S from '../../styles';

const THUMB_HEIGHT = 48;

interface VelocityProps {
	playbackRate: number;
	isDraggable?: boolean;
	onPlaybackRateChange: (rate: number) => void;
}

export function Velocity({
	playbackRate,
	isDraggable = false,
	onPlaybackRateChange
}: Readonly<VelocityProps>): JSX.Element {
	const [showDropdown, setShowDropdown] = useState<boolean>(false);
	const [scrollThumbTop, setScrollThumbTop] = useState<number>(0);
	const [isDragging, setIsDragging] = useState<boolean>(false);
	const dropdownRef = useRef<HTMLDivElement>(null);
	const listRef = useRef<HTMLDivElement>(null);
	const dragStartY = useRef<number>(0);
	const dragStartScroll = useRef<number>(0);

	function handleToggleDropdown(): void {
		setShowDropdown((prev) => !prev);
	}

	function handleOptionClick(rate: number): void {
		onPlaybackRateChange(rate);
		setShowDropdown(false);
	}

	const handleClickOutside = useCallback((event: MouseEvent): void => {
		if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
			setShowDropdown(false);
		}
	}, []);

	const handleScroll = useCallback((): void => {
		if (!listRef.current || isDragging) return;

		const { scrollTop, scrollHeight, clientHeight } = listRef.current;
		const maxScroll = scrollHeight - clientHeight;
		const thumbTrackHeight = clientHeight - THUMB_HEIGHT;
		const thumbPosition = maxScroll > 0 ? (scrollTop / maxScroll) * thumbTrackHeight : 0;

		setScrollThumbTop(thumbPosition);
	}, [isDragging]);

	const handleThumbMouseDown = useCallback((event: React.MouseEvent): void => {
		event.preventDefault();
		event.stopPropagation();

		if (!listRef.current) return;

		setIsDragging(true);
		dragStartY.current = event.clientY;
		dragStartScroll.current = listRef.current.scrollTop;
	}, []);

	const handleMouseMove = useCallback(
		(event: MouseEvent): void => {
			if (!isDragging || !listRef.current) return;

			const { scrollHeight, clientHeight } = listRef.current;
			const maxScroll = scrollHeight - clientHeight;
			const thumbTrackHeight = clientHeight - THUMB_HEIGHT;

			const deltaY = event.clientY - dragStartY.current;
			const scrollRatio = deltaY / thumbTrackHeight;
			const newScrollTop = dragStartScroll.current + scrollRatio * maxScroll;

			listRef.current.scrollTop = Math.max(0, Math.min(maxScroll, newScrollTop));

			const thumbPosition = maxScroll > 0 ? (listRef.current.scrollTop / maxScroll) * thumbTrackHeight : 0;
			setScrollThumbTop(thumbPosition);
		},
		[isDragging]
	);

	const handleMouseUp = useCallback((): void => {
		setIsDragging(false);
	}, []);

	useEffect(() => {
		if (showDropdown) {
			document.addEventListener('mousedown', handleClickOutside);

			if (listRef.current) {
				const selectedIndex = VELOCITY_OPTIONS.findIndex((option) => option.value === playbackRate);
				const totalOptions = VELOCITY_OPTIONS.length;

				if (selectedIndex !== -1) {
					const { scrollHeight, clientHeight } = listRef.current;
					const maxScroll = scrollHeight - clientHeight;
					const scrollRatio = selectedIndex / (totalOptions - 1);
					const targetScroll = maxScroll * scrollRatio;

					listRef.current.scrollTop = targetScroll;

					const thumbTrackHeight = clientHeight - THUMB_HEIGHT;
					const thumbPosition = maxScroll > 0 ? (targetScroll / maxScroll) * thumbTrackHeight : 0;
					setScrollThumbTop(thumbPosition);
				}
			}
		}

		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, [showDropdown, handleClickOutside, playbackRate]);

	useEffect(() => {
		if (isDragging) {
			document.addEventListener('mousemove', handleMouseMove);
			document.addEventListener('mouseup', handleMouseUp);
		}

		return () => {
			document.removeEventListener('mousemove', handleMouseMove);
			document.removeEventListener('mouseup', handleMouseUp);
		};
	}, [isDragging, handleMouseMove, handleMouseUp]);

	return (
		<S.ControlItem ref={dropdownRef} onMouseEnter={handleToggleDropdown} onMouseLeave={handleToggleDropdown}>
			<S.ControlButton
				icon={
					<S.HoverIcon>
						<VideoSpeedIcon width={ICON_SIZE} height={ICON_SIZE} />
					</S.HoverIcon>
				}
			/>
			{showDropdown && (
				<S.VelocityDropdownWrapper $isDraggable={isDraggable}>
					<S.VelocityDropdownList ref={listRef} onScroll={handleScroll} $isDraggable={isDraggable}>
						{VELOCITY_OPTIONS.map((option) => (
							<S.VelocityOption
								key={option.value}
								$active={playbackRate === option.value}
								onClick={() => handleOptionClick(option.value)}
							>
								{option.label}
							</S.VelocityOption>
						))}
					</S.VelocityDropdownList>
					<S.CustomScrollbar $top={scrollThumbTop} onMouseDown={handleThumbMouseDown} />
				</S.VelocityDropdownWrapper>
			)}
		</S.ControlItem>
	);
}
